import React, { useState, useMemo, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Layout,
  Menu,
  theme as antdTheme,
  Grid,
  Drawer,
  Button,
} from "antd";
import type { MenuProps } from "antd";
import { useAppContext } from "../context/AppContext";
import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";
import usePagePermission from "../hooks/usePagePermission";
import {
  SettingOutlined,
  DashboardOutlined,
  MenuOutlined,
  LogoutOutlined,
  BarChartOutlined,
  ThunderboltOutlined,
  SettingOutlined as PlantSettingOutlined,
  InfoOutlined,
  ArrowLeftOutlined,
  ContactsOutlined,
  ClockCircleOutlined,
  ApiOutlined,
  AppstoreOutlined,
  ClearOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";

import lightLogo from "../static/cdrpl.png";
import darkLogo from "../static/cdrpl.png";
import { SELECTED_PARTY_CHANGED_EVENT } from "../constants/events";

const { Sider } = Layout;
const { useBreakpoint } = Grid;

interface SidebarProps {
  collapsible: boolean;
  isMobile?: boolean;
}

const ClientNavigation = React.memo(() => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  return <Sidebar collapsible={!isMobile} isMobile={isMobile} />;
});

const Sidebar = React.memo(({ collapsible, isMobile }: SidebarProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  const { state: stateApp, appContextAction } = useAppContext();
  const { isNavMenuClose } = stateApp;
  const { navMenu } = appContextAction;
  const { theme } = useTheme();
  const { user, logout } = useAuth();
  const isDarkMode = theme.algorithm?.[0] === antdTheme.darkAlgorithm;
  const logoPath = isDarkMode ? darkLogo : lightLogo;
  const hasMultipleAssignments = (user?.partyAssignments?.length || 0) > 1;
  const canShowBackToSelection = Boolean(user?.isclient && hasMultipleAssignments);
  const projectId = localStorage.getItem("selectedProjectId");

  const handleBackNavigation = useCallback(() => {
    localStorage.removeItem("selectedPartyId");
    localStorage.removeItem("selectedPartyName");
    localStorage.removeItem("selectedProjectId");
    localStorage.removeItem("selectedProjectName");

    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent(SELECTED_PARTY_CHANGED_EVENT, {
          detail: { partyId: null },
        })
      );
    }

    const fallbackRoute = user?.isclient ? "/client-selection" : "/project-selection";
    navigate(fallbackRoute);
  }, [navigate, user?.isclient]);

  const [drawerVisible, setDrawerVisible] = useState(false);

  const handleBackToSelectionClick = useCallback(() => {
    handleBackNavigation();

    if (isMobile) {
      setDrawerVisible(false);
    }
  }, [handleBackNavigation, isMobile]);

  const canReadDashboard = usePagePermission("dashboard", "read");
  const canReadPlantDetails = usePagePermission("plant-details", "read");
  const canReadLiveData = usePagePermission("live-data", "read");
  const canReadInverters = usePagePermission("inverters", "read");
  const canReadPlantPerformance = usePagePermission("plant-performance", "read");
  const canReadAbtMeters = usePagePermission("abt-meters", "read");
  const canReadSpareList = usePagePermission("sparelist", "read");
  const canReadModuleCleaning = usePagePermission("module-cleaning", "read");
  const canReadChecklist = usePagePermission("checklist", "read");
  const canReadSldc = usePagePermission("sldc", "read");
  const canReadReports = usePagePermission("reports", "read");
  const canReadContactDetails = usePagePermission("contact-details", "read");

  const getThemeStyles = useMemo(
    () => ({
      sidebarHeader: {
        padding: isNavMenuClose && !isMobile ? "8px" : "0 16px",
        display: "flex",
        flexDirection:
          isNavMenuClose && !isMobile ? ("column" as const) : ("row" as const),
        alignItems: "center",
        justifyContent:
          isNavMenuClose && !isMobile ? "center" : "space-between",
        overflow: "hidden",
        transition: "all 0.3s ease",
        borderBottom: `1px solid ${isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)"
          }`,
        backgroundColor: isDarkMode ? "#141414" : "#fff",
      },
      logoContainer: {
        display: "flex",
        alignItems: "center",
        paddingBottom: isNavMenuClose && !isMobile ? "8px" : "0",
        justifyContent: isNavMenuClose && !isMobile ? "center" : "flex-start",
        marginBottom: isNavMenuClose && !isMobile ? "8px" : "0",
      },
      logo: {
        width: isNavMenuClose && !isMobile ? "50px" : "170px",
        height: "auto",
        transition: "width 0.3s ease",
      },
      toggleButton: {
        color: isDarkMode ? "#fff" : "#000",
        background: "transparent",
        marginTop: isNavMenuClose && !isMobile ? "12px" : undefined,
      },
      userProfile: {
        padding: "16px",
        display: "flex",
        alignItems: "center",
        borderBottom: `1px solid ${isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)"
          }`,
        marginBottom: "8px",
        backgroundColor: isDarkMode ? "#1f1f1f" : "#fff",
      },
      userInfo: {
        marginLeft: "12px",
        overflow: "hidden",
      },
      userName: {
        fontWeight: "bold",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        color: isDarkMode ? "#fff" : "#000",
      },
      userEmail: {
        fontSize: "12px",
        opacity: 0.85,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        color: isDarkMode ? "rgba(255,255,255,0.65)" : "rgba(0,0,0,0.65)",
      },
      collapsedProfile: {
        padding: "16px 0",
        display: "flex",
        justifyContent: "center",
        borderBottom: `1px solid ${isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)"
          }`,
        marginBottom: "8px",
        backgroundColor: isDarkMode ? "#1f1f1f" : "#fff",
      },
      navigationMenu: {
        flex: 1,
        overflowY: "auto" as const,
        overflowX: "hidden" as const,
        padding: "0",
        height: "auto",
        minHeight: 0,
      },
      sidebarStyle: {
        boxShadow: isDarkMode
          ? "none"
          : "0 1px 3px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.1)",
        zIndex: 1000,
        height: "100vh",
        position: "fixed" as const,
        left: 0,
        display: "flex",
        flexDirection: "column" as const,
        transition: "all 0.2s ease",
        backgroundColor: isDarkMode ? "#141414" : "#fff",
        overflow: "hidden",
      },
      backButtonWrapper: {
        padding: "12px 16px",
        borderBottom: `1px solid ${isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)"}`,
        backgroundColor: isDarkMode ? "#141414" : "#fff",
      },
    }),
    [isDarkMode, isNavMenuClose, isMobile]
  );

  const currentPath = useMemo(() => {
    if (location.pathname === "/") return "dashboard";
    return location.pathname.slice(1);
  }, [location.pathname]);

  const menuItems: MenuProps["items"] = useMemo(() => {
    const items: MenuProps["items"] = [];

    if (user?.isclient || canReadDashboard) {
      items.push({
        key: "dashboard",
        icon: <DashboardOutlined />,
        label: <Link to="/">Dashboard</Link>,
      });
    }

    if ((user?.isclient && projectId !== "ab189418-2b4f-4554-9f54-007d40779de3") || canReadPlantDetails) {
      items.push({
        key: "plant-details",
        icon: <InfoOutlined />,
        label: <Link to="/plant-details">Plant Details</Link>,
      });
    }

    if (user?.isclient || canReadLiveData) {
      items.push({
        key: "live-data",
        icon: <ClockCircleOutlined />,
        label: <Link to="/live-data">Live Data</Link>,
      });
    }

    if (user?.isclient || canReadInverters) {
      items.push({
        key: "inverters",
        icon: <ThunderboltOutlined />,
        label: <Link to="/inverters">Inverters</Link>,
      });
    }

    if ((user?.isclient && projectId !== "ab189418-2b4f-4554-9f54-007d40779de3") || canReadPlantPerformance) {
      items.push({
        key: "plant-performance",
        icon: <PlantSettingOutlined />,
        label: <Link to="/plant-performance">Plant Performance</Link>,
      });
    }

    if ((user?.isclient && projectId !== "ab189418-2b4f-4554-9f54-007d40779de3") || canReadAbtMeters) {
      items.push({
        key: "abt-meters",
        icon: <PlantSettingOutlined />,
        label: <Link to="/abt-meters">ABT Meters</Link>,
      });
    }

    if (canReadSpareList) {
      items.push({
        key: "spare-list",
        icon: <AppstoreOutlined />,
        label: <Link to="/spare-list">Spare List</Link>,
      });
    }

    if ((user?.isclient && projectId !== "ab189418-2b4f-4554-9f54-007d40779de3") || canReadModuleCleaning) {
      items.push({
        key: "module-cleaning",
        icon: <ClearOutlined />,
        label: <Link to="/module-cleaning">Module Cleaning</Link>,
      });
    }

    if ((user?.isclient && projectId !== "ab189418-2b4f-4554-9f54-007d40779de3") || canReadChecklist) {
      items.push({
        key: "checklist",
        icon: <UnorderedListOutlined />,
        label: <Link to="/checklist">Checklist</Link>,
      });
    }

    if (user?.isclient || canReadSldc) {
      items.push({
        key: "sldc-reports",
        icon: <ApiOutlined />,
        label: <Link to="/sldc-reports">SLDC</Link>,
      });
    }

    if (user?.isclient || canReadReports) {
      items.push({
        key: "reports",
        icon: <BarChartOutlined />,
        label: <Link to="/dgr-reports">Reports</Link>,
      });
    }

    items.push({ type: "divider" });

    items.push({
      key: "settings",
      icon: <SettingOutlined />,
      label: <Link to="/settings">Settings</Link>,
    });

    if (user?.isclient === false) {
      items.push({
        key: "back-to-clients",
        icon: <ArrowLeftOutlined />,
        label: "Back to Selection",
        onClick: () => handleBackNavigation(),
      });
    }

    if ((user?.isclient && projectId !== "ab189418-2b4f-4554-9f54-007d40779de3") || canReadContactDetails) {
      items.push({
        key: "contact-details",
        icon: <ContactsOutlined />,
        label: <Link to="/contact-details">Contact Details</Link>,
      });
    }

    items.push({
      key: "logout",
      icon: <LogoutOutlined />,
      label: "Logout",
      onClick: logout,
      danger: true,
    });
    return items;
  }, [
    user,
    logout,
    projectId,
    handleBackNavigation,
    canReadDashboard,
    canReadPlantDetails,
    canReadLiveData,
    canReadInverters,
    canReadPlantPerformance,
    canReadAbtMeters,
    canReadSpareList,
    canReadModuleCleaning,
    canReadChecklist,
    canReadSldc,
    canReadReports,
    canReadContactDetails,
  ]);

  const toggleCollapse = useCallback(() => {
    if (isNavMenuClose) {
      navMenu.expand();
    } else {
      navMenu.collapse();
    }
  }, [isNavMenuClose, navMenu]);

  const handleMenuClick = useCallback(
    (info: any) => {
      if (info.key === "logout") {
        logout();
      } else if (info.key === "back-to-clients") {
        handleBackNavigation();
      }

      if (isMobile) {
        setDrawerVisible(false);
      }
    },
    [logout, isMobile, handleBackNavigation]
  );

  const toggleDrawer = useCallback(() => {
    setDrawerVisible((prev) => !prev);
  }, []);

  const SidebarContent = useMemo(
    () => (
      <div
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div className="sidebar-header" style={getThemeStyles.sidebarHeader}>
          <div className="logo-container" style={getThemeStyles.logoContainer}>
            <img src={logoPath} alt="CDRPL" style={getThemeStyles.logo} />
          </div>
        </div>

        {canShowBackToSelection && (
          <div style={getThemeStyles.backButtonWrapper}>
            <Button
              icon={<ArrowLeftOutlined />}
              block
              onClick={handleBackToSelectionClick}
            >
              Back to Selection
            </Button>
          </div>
        )}

        <div className="navigation-menu" style={getThemeStyles.navigationMenu}>
          <Menu
            mode="inline"
            selectedKeys={[currentPath]}
            defaultOpenKeys={isMobile ? ["main", "hr", "settings"] : []}
            items={menuItems}
            onClick={handleMenuClick}
            style={{
              border: "none",
              background: "transparent",
              height: "100%",
            }}
            theme={isDarkMode ? "dark" : "light"}
          />
        </div>
      </div>
    ),
    [
      getThemeStyles,
      logoPath,
      isMobile,
      currentPath,
      menuItems,
      handleMenuClick,
      isDarkMode,
      canShowBackToSelection,
      handleBackToSelectionClick,
    ]
  );

  if (isMobile) {
    return (
      <>
        <Button
          type="primary"
          shape="circle"
          icon={<MenuOutlined />}
          onClick={toggleDrawer}
          style={{
            position: "fixed",
            top: "16px",
            left: "16px",
            zIndex: 999,
            boxShadow: "0 3px 6px rgba(0,0,0,0.2)",
          }}
          size="middle"
        />
        <Drawer
          placement="left"
          onClose={toggleDrawer}
          open={drawerVisible}
          width={300}
          styles={{
            body: {
              padding: 0,
              height: "100%",
              background: isDarkMode ? "#141414" : "#fff",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            },
          }}
          closable={false}
        >
          {SidebarContent}
        </Drawer>
      </>
    );
  }

  return (
    <Sider
      collapsible
      collapsed={isNavMenuClose}
      onCollapse={toggleCollapse}
      className="navigation-sidebar"
      theme={isDarkMode ? "dark" : "light"}
      width={240}
      collapsedWidth={80}
      style={getThemeStyles.sidebarStyle}
      trigger={null}
    >
      {SidebarContent}
    </Sider>
  );
});

export default ClientNavigation;
